import React, { useEffect, useState } from "react";
import "./App.css";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Chip,
  CircularProgress,
  useMediaQuery,
  Stack,
  Badge,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import MuiSlide from "@mui/material/Slide";
import { createTheme, ThemeProvider, alpha, styled, keyframes } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ConstructionIcon from "@mui/icons-material/Construction";
import TimerIcon from "@mui/icons-material/Timer";
import Shell from "./auth/Shell";
import { ToastContainer, cssTransition, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";

// GraphQL queries/mutations
import { GetPendingApprovals } from "./graphql/querys/clients copy";
import { GetInterventionNotifications } from "./graphql/querys/interventionNotifications";
import { GetMachineDocumentsExpiration } from "./graphql/querys/machines"; // Ensure this file exports the query
import { ApproveIntervention } from "./graphql/mutation/aproveIntervention";
import { ReadInterventionNotification } from "./graphql/mutation/readInterventionNotification";
import ChangeDisplay from "./components/ChangeDisplay";
import { GetExpiringMachines } from "./graphql/querys/machines"; // Add this import
import { getStoredAuthToken } from "./services/auth-service";
import { useTranslation } from "react-i18next";

// Define dramatic animations
const floatIn = keyframes`
  0% {
    opacity: 0;
    transform: translateX(40px) translateY(10px) scale(0.9);
    filter: blur(8px);
    box-shadow: 0 0 0 rgba(0, 0, 0, 0);
  }
  80% {
    opacity: 1;
    transform: translateX(-5px) translateY(-2px) scale(1.02);
    filter: blur(0);
    box-shadow: 0 15px 25px rgba(0, 0, 0, 0.25);
  }
  100% {
    transform: translateX(0) translateY(0) scale(1);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const floatOut = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
    filter: blur(10px);
  }
`;

// Custom transition for react-toastify
const glassAnimation = cssTransition({
  enter: "glass-in",
  exit: "glass-out",
  duration: 400,
  appendPosition: false,
});

// Styled ToastContainer with extreme glassmorphism (still used for other notifications)
const GlassmorphicToast = styled(ToastContainer)(({ theme }) => ({
  "&.Toastify__toast-container": {
    padding: 0,
    width: "auto",
    maxWidth: "400px",
    zIndex: 9999,
  },
  ".glass-in": {
    animation: `${floatIn} 500ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
  },
  ".glass-out": {
    animation: `${floatOut} 300ms cubic-bezier(0.47, 0, 0.745, 0.715) forwards`,
  },
  ".Toastify__toast": {
    position: "relative",
    borderRadius: 20,
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(2),
    minHeight: "65px",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    boxShadow:
      "0 10px 30px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.1) inset",
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 500,
    color: "white",
    letterSpacing: "0.015em",
    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-5px) scale(1.02)",
      boxShadow:
        "0 20px 40px rgba(0, 0, 0, 0.25), 0 2px 10px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.15) inset",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0) 60%)",
      pointerEvents: "none",
    },
  },
  // (Additional toast styling for different types can be added here)
}));

// Create a modern MUI theme
const theme = createTheme({
  palette: {
    primary: { main: "#5C6BC0" },
    secondary: { main: "#26A69A" },
    error: { main: "#EF5350" },
    success: { main: "#66BB6A" },
    background: { default: "#f8f9fa", paper: "#ffffff" },
    text: { primary: "#37474F", secondary: "#78909C" },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: { fontWeight: 600, fontSize: "1.125rem", letterSpacing: "0.0075em" },
    subtitle1: { fontWeight: 500, color: "#546E7A", fontSize: "0.9375rem" },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: "0.02em" },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, padding: "8px 16px", boxShadow: "none" },
        contained: { boxShadow: "0 4px 12px rgba(0,0,0,0.08)" },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, boxShadow: "0 12px 48px rgba(0,0,0,0.12)", overflow: "hidden" },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { padding: "20px 24px", background: "#F6F7FB" } },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: "16px 24px 24px" } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: "16px 24px", background: "#F6F7FB" } },
    },
  },
});

// Transition for the dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <MuiSlide direction="up" ref={ref} {...props} />;
});

// Modern toast message components with icons (for success, error, etc.)
const createToastContent = (message, Icon, iconColor) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Icon sx={{ color: iconColor, filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.2))" }} />
    <Typography
      sx={{
        fontWeight: 500,
        letterSpacing: "0.01em",
        fontSize: "0.9375rem",
        textShadow: "0 1px 3px rgba(0,0,0,0.2)",
        color: "white",
      }}
    >
      {message}
    </Typography>
  </Stack>
);

const showInfoToast = (message, options = {}) =>
  toast.info(createToastContent(message, InfoIcon, "#56CCF2"), { autoClose: 3000, ...options });
const showSuccessToast = (message, options = {}) =>
  toast.success(createToastContent(message, CheckCircleIcon, "#6FCF97"), { autoClose: 3000, ...options });
const showWarningToast = (message, options = {}) =>
  toast.warning(createToastContent(message, WarningIcon, "#FFBB55"), { autoClose: 3000, ...options });
const showErrorToast = (message, options = {}) =>
  toast.error(createToastContent(message, ErrorIcon, "#FF6B6B"), { autoClose: 3000, ...options });

function App() {
  const { t } = useTranslation();
  const token = getStoredAuthToken();
  const pathname = window.location.pathname;
  const isPublicPath = pathname === "/public" || pathname.startsWith("/public/");
  const [isLoading, setIsLoading] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // States for notifications
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [interventionNotifications, setInterventionNotifications] = useState([]);
  const [expiringMachines, setExpiringMachines] = useState([]);
  const [expiringRentals, setExpiringRentals] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const openNotifications = Boolean(anchorEl);
  const [currentUser, setCurrentUser] = useState(null);
  const interventionNotificationsEnabled =
    process.env.REACT_APP_ENABLE_INTERVENTION_NOTIFICATIONS === "true" ||
    ["3002", "3003"].includes(window.location.port) ||
    window.location.hostname === "app.espoaluga.pt";
  const canAccessNotificationCenter =
    currentUser?.roles?.includes("ADMIN") ||
    (interventionNotificationsEnabled && currentUser?.roles?.includes("BO"));
  const isAdmin = currentUser?.roles?.includes("ADMIN");

  // GraphQL queries and mutations
  const [fetchPendingApprovals, { data: approvalsData, loading: approvalsLoading }] = useLazyQuery(
    GetPendingApprovals,
    { fetchPolicy: "network-only" }
  );
  const [fetchInterventionNotifications, { data: interventionNotificationsData }] = useLazyQuery(
    GetInterventionNotifications,
    { fetchPolicy: "network-only", pollInterval: 30000 }
  );
  const [fetchMachineExpirations, { data: machinesData, loading: machinesLoading }] = useLazyQuery(
    GetMachineDocumentsExpiration,
    { fetchPolicy: "network-only" }
  );
  const [fetchExpiringRentals, { data: rentalsData, loading: rentalsLoading }] = useLazyQuery(
    GetExpiringMachines,
    { 
      variables: { hoursThreshold: 48 }, // Show machines with rentals ending in 48 hours
      fetchPolicy: "network-only"
    }
  );
  const [approveIntervention] = useMutation(ApproveIntervention);
  const [readInterventionNotification] = useMutation(ReadInterventionNotification);

  // State for the detailed review dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);

  // Fetch notifications only after the authenticated user's role is known.
  useEffect(() => {
    if (!token || !canAccessNotificationCenter) {
      setPendingApprovals([]);
      setInterventionNotifications([]);
      setExpiringMachines([]);
      setExpiringRentals([]);
      return;
    }

    if (interventionNotificationsEnabled) {
      fetchInterventionNotifications();
    } else {
      setInterventionNotifications([]);
    }
    fetchMachineExpirations();
    fetchExpiringRentals();

    if (isAdmin) {
      fetchPendingApprovals();
    } else {
      setPendingApprovals([]);
    }
  }, [
    token,
    canAccessNotificationCenter,
    interventionNotificationsEnabled,
    isAdmin,
    fetchInterventionNotifications,
    fetchPendingApprovals,
    fetchMachineExpirations,
    fetchExpiringRentals,
  ]);

  // Update pending approvals state
  useEffect(() => {
    if (approvalsData && approvalsData.getPendingAprovals) {
      setPendingApprovals(approvalsData.getPendingAprovals);
    }
  }, [approvalsData]);

  useEffect(() => {
    if (interventionNotificationsData?.interventionNotifications) {
      setInterventionNotifications(interventionNotificationsData.interventionNotifications);
    }
  }, [interventionNotificationsData]);

  // Update machine document expiration state
  useEffect(() => {
    if (machinesData && machinesData.machineDocumentsExpiration) {
      setExpiringMachines(machinesData.machineDocumentsExpiration);
    }
  }, [machinesData]);

  // Update expiring rentals state
  useEffect(() => {
    if (rentalsData?.getMachineRentingExpiration) {
      setExpiringRentals(rentalsData.getMachineRentingExpiration);
    }
  }, [rentalsData]);

  // Notification menu handlers
  const handleOpenNotifications = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseNotifications = () => {
    setAnchorEl(null);
  };

  const handleOpenInterventionNotification = async (notification) => {
    try {
      await readInterventionNotification({ variables: { id: notification._id } });
      setInterventionNotifications((current) =>
        current.filter((item) => item._id !== notification._id)
      );
    } catch (error) {
      showErrorToast(t("APP.ERROR_READ_NOTIFICATION"));
    }

    handleCloseNotifications();
    navigate(`/machines/${notification.machineId}`);
  };

  // Open the detailed dialog for a pending approval
  const handleOpenDialog = (approval) => {
    setSelectedApproval(approval);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedApproval(null);
  };

  const handleApprove = async () => {
    if (!selectedApproval) return;
    setIsLoading(true);
    try {
      await approveIntervention({
        variables: { interventionId: selectedApproval._id, type: "approve" },
      });
      toast.dismiss(`approval-${selectedApproval._id}`);
      showSuccessToast(t("APP.INTERVENTION_APPROVED"));
      handleCloseDialog();
      fetchPendingApprovals();
    } catch (err) {
      showErrorToast(t("APP.ERROR_APPROVE_INTERVENTION"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisapprove = async () => {
    if (!selectedApproval) return;
    setIsLoading(true);
    try {
      await approveIntervention({
        variables: { interventionId: selectedApproval._id, type: "disapprove" },
      });
      toast.dismiss(`approval-${selectedApproval._id}`);
      showSuccessToast(t("APP.CHANGES_REJECTED"));
      handleCloseDialog();
      fetchPendingApprovals();
    } catch (err) {
      showErrorToast(t("APP.ERROR_REJECT_INTERVENTION"));
    } finally {
      setIsLoading(false);
    }
  };

  // (Optional) For testing other toast notifications
  const showTestToasts = () => {
    showInfoToast(t("APP.TEST_INFO"));
    setTimeout(() => showSuccessToast(t("APP.TEST_SUCCESS")), 1000);
    setTimeout(() => showWarningToast(t("APP.TEST_WARNING")), 2000);
    setTimeout(() => showErrorToast(t("APP.TEST_ERROR")), 3000);
  };

  // Add this helper function at the top level of your file
  const formatTimeRemaining = (endDate) => {
    const now = new Date();
    const diffHours = Math.round((endDate - now) / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      return t("APP.HOURS", { count: diffHours });
    } else {
      const days = Math.floor(diffHours / 24);
      const remainingHours = diffHours % 24;
      return t("APP.DAYS_HOURS", { days, hours: remainingHours });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Shell onSessionResolved={setCurrentUser} />

      {/* Notification Bell Icon with Badge (sum of all notifications) */}
      {canAccessNotificationCenter && (
        <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 1300 }}>
          <IconButton onClick={handleOpenNotifications} color="inherit">
            <Badge 
              badgeContent={pendingApprovals.length + interventionNotifications.length + expiringMachines.length + expiringRentals.length}
              color="error"
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Box>
      )}

      {/* Notification Menu */}
      <Menu
        anchorEl={anchorEl}
        open={openNotifications}
        onClose={handleCloseNotifications}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {pendingApprovals.length === 0 && interventionNotifications.length === 0 && expiringMachines.length === 0 && expiringRentals.length === 0 ? (
          <MenuItem onClick={handleCloseNotifications}>
            <Typography variant="body2">{t("APP.NO_NOTIFICATIONS")}</Typography>
          </MenuItem>
        ) : (
          <>
            {interventionNotificationsEnabled && interventionNotifications.map((notification) => (
              <MenuItem
                key={`intervention-${notification._id}`}
                onClick={() => handleOpenInterventionNotification(notification)}
              >
                <ConstructionIcon sx={{ mr: 1, color: "#26A69A" }} />
                <Typography variant="body2">
                  {t("APP.INTERVENTION_COMPLETED", { name: notification.machineName })}
                </Typography>
              </MenuItem>
            ))}

            {interventionNotifications.length > 0 && pendingApprovals.length > 0 && <Divider />}

            {pendingApprovals.map((approval) => (
              <MenuItem
                key={`approval-${approval._id}`}
                onClick={() => {
                  handleOpenDialog(approval);
                  handleCloseNotifications();
                }}
              >
                <PendingIcon sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {t("APP.PENDING_APPROVAL", { name: approval.machineInfo.name })}
                </Typography>
              </MenuItem>
            ))}
            
            {(pendingApprovals.length > 0 && (expiringMachines.length > 0 || expiringRentals.length > 0)) && <Divider />}
            
            {expiringMachines.map((machine) => (
              <MenuItem
                key={`machine-${machine._id}`}
                onClick={() => {
                  handleCloseNotifications();
                  navigate(`/machines/${machine._id}`);
                }}
              >
                <InfoIcon sx={{ mr: 1, color: "#FFB74D" }} />
                <Typography variant="body2">
                  {t("APP.EXPIRING_DOCUMENTS", { name: machine.name })}
                </Typography>
              </MenuItem>
            ))}

            {(expiringMachines.length > 0 && expiringRentals.length > 0) && <Divider />}

            {expiringRentals.map((machine) => (
              <MenuItem
                key={`rental-${machine._id}`}
                onClick={() => {
                  handleCloseNotifications();
                  navigate(`/machines/${machine._id}`);
                }}
              >
                <TimerIcon sx={{ mr: 1, color: "#F44336" }} />
                <Typography variant="body2">
                  {t("APP.EXPIRING_RENTAL", {
                    name: machine.name,
                    category: machine.category ? `(${machine.category})` : '',
                    ref: machine.ref,
                  })}
                </Typography>
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      {/* Toast container for other notifications */}
      <GlassmorphicToast
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        transition={glassAnimation}
        limit={3}
      />

      {/* Detailed review dialog for pending approvals */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        TransitionComponent={Transition}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">{t("APP.CHANGES_REVIEW")}</Typography>
            <Chip
              label={t("APP.REQUIRES_APPROVAL")}
              color="primary"
              size="small"
              variant="outlined"
              icon={<PendingIcon fontSize="small" />}
            />
          </Stack>
          <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {approvalsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : selectedApproval ? (
            <Box sx={{ py: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t("APP.INTERVENTION_ID")}
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {selectedApproval._id}
                </Typography>
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                {t("APP.PROPOSED_CHANGES")}
              </Typography>

              <ChangeDisplay
                pendingChanges={selectedApproval.pendingChanges}
                originalValues={selectedApproval}
              />
            </Box>
          ) : (
            <Typography>{t("APP.NO_CHANGE_SELECTED")}</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit" disabled={isLoading}>
            {t("ACTIONS.CANCEL")}
          </Button>
          <Button
            onClick={handleDisapprove}
            variant="outlined"
            color="error"
            disabled={!selectedApproval || isLoading}
            startIcon={<CancelIcon />}
          >
            {isLoading ? <CircularProgress size={20} color="error" /> : t("APP.REJECT")}
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="primary"
            disabled={!selectedApproval || isLoading}
            startIcon={<CheckCircleIcon />}
          >
            {isLoading ? <CircularProgress size={20} color="inherit" /> : t("APP.APPROVE")}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default App;
